import { equals, reject, uniq } from 'ramda';

import { Question, Response, ResponseOption } from '@bighealth/types';

import { QuizInfo } from 'components/forms/InputField';

import {
  FormikPersistableState,
  QuestionId,
  QuestionResponseAction,
  QuestionResponseActions,
} from './actions';

export interface QuestionResponseState {
  responseOptionsToSubmit: Record<QuestionId, Response>;
  questions: Question[];
  correctAnswers: Record<QuestionId, QuizInfo>;
  quizAttemptsByPath: { path: string; attempt: ResponseOption[] }[];
  formikState: Record<string, FormikPersistableState>;
  currentFocusedQuestionId: number | undefined;
  latestQuestionId: number | undefined;
}

export const initialQuestionResponseState: QuestionResponseState = {
  responseOptionsToSubmit: {},
  questions: [],
  correctAnswers: {},
  quizAttemptsByPath: [],
  formikState: {},
  currentFocusedQuestionId: undefined,
  latestQuestionId: undefined,
};

export const questionResponseReducer = (
  state = initialQuestionResponseState,
  action: QuestionResponseAction
): QuestionResponseState => {
  switch (action.type) {
    case QuestionResponseActions.ADD_TO_SUBMIT: {
      return {
        ...state,
        responseOptionsToSubmit: {
          ...state.responseOptionsToSubmit,
          ...action.payload,
        },
      };
    }
    case QuestionResponseActions.CLEAR_TO_SUBMIT:
      return {
        ...state,
        responseOptionsToSubmit: {},
      };
    case QuestionResponseActions.ADD_CORRECT_ANSWERS: {
      return {
        ...state,
        correctAnswers: { ...state.correctAnswers, ...action.payload },
      };
    }
    case QuestionResponseActions.CLEAR_CORRECT_ANSWERS:
      return {
        ...state,
        correctAnswers: {},
      };
    case QuestionResponseActions.PUSH_QUIZ_ANSWER_BY_SCENE_SET: {
      if (state.quizAttemptsByPath.find(el => equals(el, action.payload))) {
        return state; // Skip;
      }
      return {
        ...state,
        quizAttemptsByPath: [...state.quizAttemptsByPath, action.payload],
      };
    }
    case QuestionResponseActions.CLEAR_QUIZ_ANSWER_BY_SCENE_SET: {
      const pathToClear = action.payload;
      return {
        ...state,
        quizAttemptsByPath: reject(
          el => el.path === pathToClear,
          state.quizAttemptsByPath
        ),
      };
    }
    case QuestionResponseActions.PUSH: {
      return {
        ...state,
        questions: uniq([...state.questions, action.payload]),
      };
    }
    case QuestionResponseActions.CLEAR: {
      return {
        ...state,
        questions: [],
      };
    }
    case QuestionResponseActions.STORE_FORMIK_STATE_BY_PATH: {
      return {
        ...state,
        formikState: {
          ...state.formikState,
          [action.payload.path]: action.payload.state,
        },
      };
    }
    case QuestionResponseActions.SET_CURRENT_FOCUSED_QUESTION: {
      return {
        ...state,
        currentFocusedQuestionId: action.payload,
      };
    }
    case QuestionResponseActions.SET_LATEST_QUESTION: {
      return {
        ...state,
        latestQuestionId: action.payload,
      };
    }
    default:
      return state;
  }
};
